import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SignatureWorkflow = ({ document, stakeholders, currentUser, onSignDocument, onRejectDocument }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authStep, setAuthStep] = useState('password');
  const [authData, setAuthData] = useState({
    password: '',
    privateKey: '',
    otp: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const getStakeholderStatus = (stakeholder) => {
    if (stakeholder?.signedAt) return 'completed';
    if (stakeholder?.rejectedAt) return 'rejected';
    if (stakeholder?.id === currentUser?.id) return 'current';
    return 'pending';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <Icon name="CheckCircle" size={20} className="text-success" />;
      case 'rejected':
        return <Icon name="XCircle" size={20} className="text-error" />;
      case 'current':
        return <Icon name="Clock" size={20} className="text-warning" />;
      default:
        return <Icon name="Circle" size={20} className="text-muted-foreground" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Signed';
      case 'rejected':
        return 'Rejected';
      case 'current':
        return 'Your Turn';
      default:
        return 'Pending';
    }
  };

  const handleSignClick = () => {
    setShowAuthModal(true);
    setAuthStep('password');
  };

  const handleAuthNext = () => {
    if (authStep === 'password') {
      setAuthStep('privateKey');
    } else if (authStep === 'privateKey') {
      setAuthStep('otp');
    } else {
      handleFinalSign();
    }
  };

  const handleFinalSign = async () => {
    setIsProcessing(true);
    
    // Simulate signature process
    setTimeout(() => {
      onSignDocument({
        userId: currentUser?.id,
        timestamp: new Date()?.toISOString(),
        blockchainHash: `0x${Math.random()?.toString(16)?.substr(2, 64)}`,
        authMethod: 'private_key'
      });
      setIsProcessing(false);
      setShowAuthModal(false);
      setAuthData({ password: '', privateKey: '', otp: '' });
    }, 2000);
  };

  const handleReject = () => {
    onRejectDocument({
      userId: currentUser?.id,
      timestamp: new Date()?.toISOString(),
      reason: 'Document requires revision'
    });
  };

  const completedSignatures = stakeholders?.filter(s => s?.signedAt)?.length;
  const totalSignatures = stakeholders?.length;
  const progressPercentage = (completedSignatures / totalSignatures) * 100;

  const canSign = document?.status === 'pending_signature' && 
                  !stakeholders?.find(s => s?.id === currentUser?.id)?.signedAt;

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Signature Progress</h3>
          <span className="text-sm font-medium text-muted-foreground">
            {completedSignatures} of {totalSignatures} completed
          </span>
        </div>

        <div className="space-y-4">
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">Total Stakeholders:</span>
              <span className="font-medium text-foreground">{totalSignatures}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">Time Remaining:</span>
              <span className="font-medium text-foreground">5 days</span>
            </div>
          </div>
        </div>
      </div>
      {/* Stakeholder List */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Stakeholder Status</h3>
        
        <div className="space-y-4">
          {stakeholders?.map((stakeholder, index) => {
            const status = getStakeholderStatus(stakeholder);
            return (
              <div key={stakeholder?.id} className="flex items-center space-x-4 p-3 rounded-lg bg-muted/30">
                <div className="flex-shrink-0">
                  {getStatusIcon(status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="font-medium text-foreground">{stakeholder?.name}</p>
                    {status === 'current' && (
                      <span className="px-2 py-1 text-xs font-medium bg-warning/20 text-warning rounded-full">
                        Your Turn
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{stakeholder?.role}</p>
                  <p className="text-xs text-muted-foreground">{stakeholder?.email}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className={`text-sm font-medium ${
                    status === 'completed' ? 'text-success' :
                    status === 'rejected' ? 'text-error' :
                    status === 'current' ? 'text-warning' : 'text-muted-foreground'
                  }`}>
                    {getStatusText(status)}
                  </p>
                  {stakeholder?.signedAt && (
                    <p className="text-xs text-muted-foreground">
                      {new Date(stakeholder.signedAt)?.toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Signature Actions */}
      {canSign && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Your Signature Required</h3>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-warning">Action Required</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Please review the document carefully before signing. Your digital signature will be 
                  recorded on the blockchain for legal validity.
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="default"
                onClick={handleSignClick}
                iconName="PenTool"
                iconPosition="left"
                className="flex-1"
              >
                Sign Document
              </Button>
              <Button
                variant="outline"
                onClick={handleReject}
                iconName="X"
                iconPosition="left"
              >
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Blockchain Verification */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Blockchain Verification</h3>
        
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="text-sm text-muted-foreground">Document hash verified on blockchain</span>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Last updated: {new Date()?.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="Hash" size={16} className="text-muted-foreground" />
            <span className="text-xs font-mono text-muted-foreground">
              0xa1b2c3d4e5f6789012345678901234567890abcdef
            </span>
          </div>
        </div>
      </div>
      {/* Authentication Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">
                  {authStep === 'password' ? 'Verify Password' :
                   authStep === 'privateKey' ? 'Private Key Authentication' : 'Two-Factor Authentication'}
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowAuthModal(false)}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>

              <div className="space-y-4">
                {authStep === 'password' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Account Password
                    </label>
                    <input
                      type="password"
                      placeholder="Enter your password"
                      value={authData?.password}
                      onChange={(e) => setAuthData(prev => ({ ...prev, password: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use: admin123
                    </p>
                  </div>
                )}

                {authStep === 'privateKey' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Private Key
                    </label>
                    <textarea
                      placeholder="Enter your private key"
                      value={authData?.privateKey}
                      onChange={(e) => setAuthData(prev => ({ ...prev, privateKey: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent h-24 font-mono text-xs"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use: 0x1234567890abcdef
                    </p>
                  </div>
                )}

                {authStep === 'otp' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={authData?.otp}
                      onChange={(e) => setAuthData(prev => ({ ...prev, otp: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent text-center font-mono text-lg"
                      maxLength={6}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use: 123456
                    </p>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowAuthModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleAuthNext}
                    loading={isProcessing}
                    className="flex-1"
                  >
                    {authStep === 'otp' ? 'Sign Document' : 'Continue'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureWorkflow;